import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SIDEBAR_MENUS, ROLE_LABELS } from "@/constants/roles";
import { Logo } from "@/components/Logo";
import { DASH } from "@/constants/testIds";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/pages/notifications/NotificationPages";
import { usePageTitle } from "@/hooks/usePageTitle";

function SidebarItem({ item, active, onClick }) {
  const Icon = Icons[item.icon] || Icons.Circle;
  const disabled = item.disabled;
  const base =
    "group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all";
  if (disabled) {
    return (
      <div
        className={`${base} cursor-not-allowed text-slate-400`}
        data-testid={DASH.sidebarLink(item.label.toLowerCase().replace(/\s+/g, "-"))}
        title="Available in a future phase"
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1">{item.label}</span>
        {item.badge && (
          <Badge className="rounded-full bg-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-100">
            {item.badge}
          </Badge>
        )}
      </div>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={`${base} ${
        active
          ? "bg-slate-50 text-[#101876]"
          : "text-slate-600 hover:bg-slate-50 hover:text-[#101876]"
      }`}
      data-testid={DASH.sidebarLink(item.label.toLowerCase().replace(/\s+/g, "-"))}
    >
      {active && (
        <span className="absolute inset-y-1 left-0 w-1 rounded-r-full bg-[#329112]" />
      )}
      <Icon className="h-4 w-4" />
      <span className="flex-1">{item.label}</span>
    </Link>
  );
}

function SidebarContent({ role, pathname, onNavigate, orgLogo }) {
  const items = SIDEBAR_MENUS[role] || [];
  const [query, setQuery] = useState("");
  const navRef = useRef(null);

  // Preserve sidebar scroll across route changes. Route changes cause
  // <SidebarContent> to re-render — the container's scrollTop can flip back
  // to 0. We only record scrollTop during real user gestures (wheel/touch
  // scroll or scrollbar-drag via pointerdown), then restore on every render.
  const SCROLL_KEY = `slc.sidebar.scroll.${role || "anon"}`;
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || "0", 10);
    if (saved > 0 && Math.abs(el.scrollTop - saved) > 2) {
      el.scrollTop = saved;
    }
    const record = () => {
      // Debounce a frame so the DOM's scrollTop has settled after the gesture.
      requestAnimationFrame(() => {
        sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
      });
    };
    el.addEventListener("wheel", record, { passive: true });
    el.addEventListener("touchmove", record, { passive: true });
    el.addEventListener("pointerup", record, { passive: true });
    el.addEventListener("keyup", record, { passive: true });
    return () => {
      el.removeEventListener("wheel", record);
      el.removeEventListener("touchmove", record);
      el.removeEventListener("pointerup", record);
      el.removeEventListener("keyup", record);
    };
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <>
      <div className="border-b border-slate-200 px-4 py-4 flex items-center justify-center">
        <Logo variant="compact" orgLogo={orgLogo} />
      </div>
      <div className="border-b border-slate-100 px-3 py-3">
        <label className="relative block">
          <Icons.Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search menu…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-7 text-xs text-slate-700 placeholder:text-slate-400 focus:border-[#101876] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#101876]/10"
            data-testid="sidebar-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Clear search"
              data-testid="sidebar-search-clear"
            >
              <Icons.X className="h-3 w-3" />
            </button>
          )}
        </label>
      </div>
      <nav
        ref={navRef}
        className="flex-1 space-y-1 overflow-y-auto px-3 py-4 scrollbar-thin"
      >
        {filtered.length === 0 ? (
          <div className="px-2 py-6 text-center text-xs text-slate-400" data-testid="sidebar-empty">
            No menu items match &ldquo;{query}&rdquo;.
          </div>
        ) : (
          filtered.map((item) => (
            <SidebarItem
              key={item.to}
              item={item}
              active={pathname === item.to}
              onClick={onNavigate}
            />
          ))
        )}
      </nav>
    </>
  );
}

export function DashboardLayout({ children, title, breadcrumbs = [] }) {
  usePageTitle(title);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const roleLabel = ROLE_LABELS[user?.role_code] || "Member";

  const initials =
    (user?.first_name?.[0] || "") + (user?.last_name?.[0] || "") || "U";

  useEffect(() => {
    if (user?.role_code !== "super_admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/notifications", { params: { unread_only: true } });
        if (!cancelled) setUnread(data?.data?.unread_count || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role_code]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar – desktop */}
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex"
        data-testid={DASH.sidebar}
      >
        <SidebarContent role={user?.role_code} pathname={location.pathname} orgLogo={user?.organization_logo} />
      </aside>

      {/* Sidebar – mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              key="drawer"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "tween", duration: 0.2 }}
              className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-slate-200 bg-white lg:hidden"
            >
              <SidebarContent
                role={user?.role_code}
                pathname={location.pathname}
                onNavigate={() => setMobileOpen(false)}
                orgLogo={user?.organization_logo}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main column */}
      <div className="flex min-h-screen min-w-0 flex-col lg:pl-64">
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur md:px-6"
          data-testid={DASH.topbar}
        >
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid={DASH.mobileMenuToggle}
          >
            <Icons.Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="font-semibold uppercase tracking-[0.15em] text-[#329112]">
                {roleLabel}
              </span>
              {breadcrumbs.map((b, i) => (
                <span key={i} className="flex items-center gap-2">
                  <Icons.ChevronRight className="h-3 w-3 text-slate-300" />
                  <span>{b}</span>
                </span>
              ))}
            </div>
            <h1 className="mt-0.5 truncate text-lg font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              {title}
            </h1>
          </div>
          <CartTopbarLink />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-3 rounded-full border border-slate-200 bg-white p-1 pr-3 hover:border-[#101876]/40 transition"
                data-testid={DASH.userMenuButton}
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#101876] text-xs font-bold text-white">
                    {initials.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden text-left leading-tight md:block">
                  <div className="text-sm font-semibold text-slate-900">{user?.full_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {user?.organization_name || "Steward"}
                  </div>
                </div>
                <Icons.ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid={DASH.userMenu}>
              <DropdownMenuLabel className="text-xs text-slate-500">
                Signed in as
                <div className="mt-0.5 text-sm font-semibold text-slate-900 truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid={DASH.profileLink}>
                <Link to="/app/settings/profile"><Icons.User className="mr-2 h-4 w-4" />Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid={DASH.changePwdLink}>
                <Link to="/app/settings/change-password"><Icons.KeyRound className="mr-2 h-4 w-4" />Change Password</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid={DASH.settingsLink}>
                <Link to="/app/settings"><Icons.Settings className="mr-2 h-4 w-4" />Account Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid={DASH.logoutBtn}>
                <Icons.LogOut className="mr-2 h-4 w-4" />Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}

// Cart topbar link — badge shows count from authed cart API (or 0).
// Clicking navigates to the learner's authenticated cart page.
function CartTopbarLink() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!user) return;
      try {
        const { data } = await api.get("/learner/cart");
        if (alive) setCount(data?.data?.items?.length || 0);
      } catch { /* silent */ }
    };
    load();
    const on = () => load();
    window.addEventListener("cart:updated", on);
    return () => { alive = false; window.removeEventListener("cart:updated", on); };
  }, [user?.id]);

  return (
    <Link
      to="/cart"
      title="Cart"
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]"
      data-testid="topbar-cart"
    >
      <Icons.ShoppingCart className="h-4 w-4" />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#329112] px-1 text-[10px] font-black text-white"
          data-testid="topbar-cart-badge"
        >
          {count}
        </span>
      )}
    </Link>
  );
}
